import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  saveLogin,
  fetchData,
  resetOldConnectionsStatus,
} from "../../redux/slices/mainDataSlice";
import { toast } from "react-hot-toast";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSchool,
  faArrowRight,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Line, Doughnut } from "react-chartjs-2";
import PrettyTooltip from "../../components/Tooltip";

// Register ChartJS
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Utility: Format date string to login info
const formatLoginInfo = (dateStr) => {
  if (!dateStr) return { time: "--:--", period: "", date: "", day: "" };
  try {
    const dateObj = new Date(dateStr);
    // Use a regex for splitting to handle potential non-breaking spaces in some environments
    const timeStr = dateObj.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    const [time, period] = timeStr.split(/\s+/);
    const date = dateObj
      .toLocaleDateString("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      })
      .replace(/\//g, "-");
    const day = dateObj.toLocaleDateString("ar-EG", { weekday: "long" });
    return { time, period, date, day };
  } catch {
    return { time: "--:--", period: "", date: "", day: "" };
  }
};

const Home = () => {
  const schoolsData = useSelector(
    (state) => state.main.data?.schoolData || [],
    (a, b) => JSON.stringify(a) === JSON.stringify(b)
  );
  const expirydateData = useSelector(
    (state) => state.main.data?.expirydateData
  )?.[0];
  const allTeachers = useSelector(
    (state) => state.main.data?.allteachers || [],
    (a, b) => JSON.stringify(a) === JSON.stringify(b)
  );
  const alfarestext = useSelector(
    (state) => state.main.data?.alfarestext || [],
    (a, b) => JSON.stringify(a) === JSON.stringify(b)
  );
  const isAuth = useSelector((state) => state.main.isAuthenticated);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [weeklyActivity, setWeeklyActivity] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isCleaningOldConnections, setIsCleaningOldConnections] = useState(false);
  const [selectedDayData, setSelectedDayData] = useState(null);

  // --- Quick Actions Logic ---
  const allAvailableActions = [
    { id: 'schools', label: "جميع المدارس", icon: "fa-school", path: "/schools", color: "#1d7567" },
    { id: 'add-school', label: "إضافة مدرسة", icon: "fa-plus", path: "/add-school", color: "#10b981" },
    { id: 'finance', label: "المالية", icon: "fa-dollar", path: "/finance", color: "#f59e0b" },
    { id: 'find-teachers', label: "البحث عن موظف او مدرسة", icon: "fa-search", path: "/find-teachers", color: "#8b5cf6" },
    { id: 'archive', label: "الأرشيف", icon: "fa-archive", path: "/archive", color: "#64748b" },
    { id: 'school-notes', label: "الرسائل", icon: "fa-envelope", path: "/school-notes", color: "#ec4899" },
    { id: 'annual-events', label: "الفعاليات", icon: "fa-calendar-star", path: "/annual-events", color: "#f43f5e" },
    { id: 'notifications', label: "الإشعارات", icon: "fa-bell", path: "/notifications", color: "#facc15" },
    { id: 'school-constants', label: "الثوابت", icon: "fa-gears", path: "/school-constants", color: "#475569" },
    { id: 'publish-version', label: "تحديثات", icon: "fa-cloud-arrow-up", path: "/publish-new-version", color: "#06b6d4" },
    { id: 'holidays', label: "العطل والمناسبات", icon: "fa-umbrella-beach", path: "/holidays", color: "#fb923c" },
    { id: 'tasks-holidays', label: "أيام المهمات", icon: "fa-person-walking-arrow-right", path: "/tasks-holidays", color: "#6366f1" },
    { id: 'school-events', label: "مناسبات المدرسة", icon: "fa-calendar-day", path: "/school-events", color: "#f97316" },
    { id: 'transferred', label: "المنقولين", icon: "fa-right-left", path: "/transferred-teachers", color: "#2dd4bf" },
  ];

  const defaultActionIds = ["schools", "find-teachers", "finance"];

  const [quickActions, setQuickActions] = useState(() => {
    const saved = localStorage.getItem("quickActions_v3");
    if (saved) return JSON.parse(saved);
    return defaultActionIds
      .map((id) => allAvailableActions.find((a) => a.id === id))
      .filter(Boolean);
  });

  const [isEditingActions, setIsEditingActions] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);

  const saveActions = (newActions) => {
    setQuickActions(newActions);
    localStorage.setItem("quickActions_v3", JSON.stringify(newActions));
  };

  const handleMoveAction = (index, direction) => {
    const newActions = [...quickActions];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newActions.length) return;
    [newActions[index], newActions[targetIndex]] = [newActions[targetIndex], newActions[index]];
    saveActions(newActions);
  };

  const handleRemoveAction = (index) => {
    const newActions = quickActions.filter((_, i) => i !== index);
    saveActions(newActions);
  };

  const handleAddAction = (action) => {
    if (quickActions.some(a => a.id === action.id)) {
      toast.error("موجود بالفعل");
      return;
    }
    saveActions([...quickActions, action]);
    setShowAddMenu(false);
    toast.success("تمت الإضافة");
  };

  const handleResetActions = () => {
    saveActions(allAvailableActions.slice(0, 6));
    setIsEditingActions(false);
    toast.success("تمت الاستعادة");
  };

  useEffect(() => {
    const storedUserCredentials = JSON.parse(
      localStorage.getItem("userCredentials")
    );
    if (!isAuth && storedUserCredentials) {
      dispatch(saveLogin(storedUserCredentials));
      dispatch(fetchData());
    } else if (isAuth) {
      dispatch(fetchData(true));
    } else {
      navigate("/");
    }

    // Clean up any existing tooltip on mount
    const existingTooltip = document.body.querySelector(".weekly-activity-tooltip");
    if (existingTooltip) {
      existingTooltip.remove();
    }
  }, [isAuth, dispatch, navigate]);

  // تحديث تلقائي دوري لبرس (خاصة حالة الاتصال) بدون الحاجة لعمل Refresh
  useEffect(() => {
    if (!isAuth) return;

    const intervalId = setInterval(() => {
      dispatch(fetchData(true));
    }, 30000); // كل 30 ثانية

    return () => clearInterval(intervalId);
  }, [isAuth, dispatch]);

  // جلب نشاط المدارس الأسبوعي (آخر 7 أيام) اعتمادًا على last_used
  useEffect(() => {
    const fetchWeeklyActivity = async () => {
      try {
        const credentials = JSON.parse(localStorage.getItem("userCredentials"));
        const token = credentials?.token;
        if (!token) return;

        const res = await axios.get(
          `https://api.alfares.cloud/api/alfares/weekly-activity/${token}`
        );
        setWeeklyActivity(res.data?.days || []);
      } catch (err) {
        // في حال فشل الإحصائية لا نكسر الصفحة، فقط نترك الرسم على الوضع الافتراضي
        console.error("Failed to fetch weekly activity", err);
      }
    };

    if (isAuth) {
      fetchWeeklyActivity();
      const intervalId = setInterval(fetchWeeklyActivity, 60000); // تحديث كل دقيقة
      return () => clearInterval(intervalId);
    }
  }, [isAuth]);

  // --- Statistics Calculations ---
  const activeSchools = useMemo(
    () => schoolsData.filter((s) => s.onoff === "on").length,
    [schoolsData]
  );
  const inactiveSchools = useMemo(
    () => schoolsData.filter((s) => s.onoff === "off").length,
    [schoolsData]
  );
  const totalSchools = useMemo(() => schoolsData.length, [schoolsData]);
  const connectedSchoolsList = useMemo(() => {
    return [...schoolsData]
      .filter((s) => Number(s.school_status) === 1)
      .sort((a, b) => {
        const dateA = a.login_time ? new Date(a.login_time) : new Date(0);
        const dateB = b.login_time ? new Date(b.login_time) : new Date(0);
        return dateB - dateA;
      });
  }, [schoolsData]);
  const connectedNow = useMemo(
    () => connectedSchoolsList.length,
    [connectedSchoolsList]
  );

  // Find secretary names for connected schools
  const connectedSchoolsDetailed = useMemo(() => {
    return connectedSchoolsList.map((school) => {
      const secretary = allTeachers.find(
        (t) =>
          String(t.rgmw) === String(school.rgmw) &&
          (t.W_Num === 3 || t.W_Num === 4)
      );

      let label = "سكرتير/ة";
      if (secretary) {
        label = Number(secretary.emp_gender) === 1 ? "سكرتير" : "سكرتيرة";
      }

      return {
        ...school,
        secretaryName: secretary ? secretary.emp_name : "غير محدد",
        secretaryLabel: label,
        loginInfo: formatLoginInfo(school.login_time),
      };
    });
  }, [connectedSchoolsList, allTeachers]);

  const filteredConnectedSchools = useMemo(() => {
    if (!searchTerm.trim()) return connectedSchoolsDetailed;
    const lowerSearch = searchTerm.toLowerCase();
    return connectedSchoolsDetailed.filter(
      (s) =>
        s.schoolname?.toLowerCase().includes(lowerSearch) ||
        s.secretaryName?.toLowerCase().includes(lowerSearch) ||
        String(s.rgmw).includes(lowerSearch)
    );
  }, [connectedSchoolsDetailed, searchTerm]);

  const unreadMessagesCount = useMemo(
    () => alfarestext.filter((n) => n.has_seen === 0).length,
    [alfarestext]
  );

  // --- Chart Data & Options ---
  const weeklyActivityData = useMemo(() => {
    if (!weeklyActivity || weeklyActivity.length === 0) return null;

    const labels = weeklyActivity.map((item) => {
      const d = new Date(item.date);
      return d.toLocaleDateString("ar-EG", { weekday: "short" });
    });

    const data = weeklyActivity.map((item) => Number(item.count) || 0);

    const dates = weeklyActivity.map((item) => item.date);

    return { labels, data, dates };
  }, [weeklyActivity]);

  // خريطة: تاريخ -> أسماء المدارس التي كان آخر دخول لها في هذا التاريخ
  const weeklyActivitySchoolsByDate = useMemo(() => {
    if (!weeklyActivity || weeklyActivity.length === 0 || !schoolsData?.length)
      return {};

    const map = {};

    weeklyActivity.forEach((item) => {
      const dateKey = item.date;
      const schoolsForDay = schoolsData
        .filter((s) => {
          if (!s.login_time) return false;
          const schoolDate = String(s.login_time).slice(0, 10);
          return schoolDate === dateKey;
        })
        .map((s) => ({ name: s.schoolname, rgmw: s.rgmw }));

      map[dateKey] = schoolsForDay;
    });

    return map;
  }, [weeklyActivity, schoolsData]);

  const chartOptions = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: "#94a3b8", font: { family: "Somar" } },
        },
        y: {
          grid: { color: "rgba(148, 163, 184, 0.1)" },
          ticks: { color: "#94a3b8", font: { family: "Somar" } },
        },
      },
      onHover: (event, activeElements, chart) => {
        const canvas = chart?.canvas || event?.native?.target;
        if (!canvas) return;
        canvas.style.cursor = activeElements && activeElements.length
          ? "pointer"
          : "default";
      },
      onClick: (event, activeElements, chart) => {
        if (!activeElements || !activeElements.length) {
          setSelectedDayData(null);
          return;
        }

        const element = activeElements[0];
        const datasetIndex = element.datasetIndex;
        const index = element.index;
        const dataset = chart.data.datasets[datasetIndex];
        const point = dataset.data[index];

        const label = chart.data.labels[index];

        let value;
        let schools = [];

        if (point && typeof point === "object") {
          value = point.y;
          schools = Array.isArray(point.schools) ? point.schools : [];
        } else {
          value = point;
        }

        if (typeof value === "number") {
          value = `${value} مدرسة`;
        }

        setSelectedDayData({
          label,
          value,
          schools
        });
      },
    };
  }, []);

  const donutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "75%",
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "rgba(255, 255, 255, 0.96)",
        padding: 12,
        cornerRadius: 8,
      },
    },
  };

  const lineChartData = useMemo(() => {
    const labels = weeklyActivityData?.labels || [
      "S",
      "M",
      "T",
      "W",
      "T",
      "F",
      "S",
    ];

    let data;

    if (weeklyActivityData && weeklyActivityData.data && weeklyActivityData.dates) {
      data = weeklyActivityData.data.map((value, index) => {
        const dateKey = weeklyActivityData.dates[index];
        const schoolsForDay = weeklyActivitySchoolsByDate[dateKey] || [];

        return {
          x: labels[index],
          y: value,
          dateKey,
          schools: schoolsForDay,
        };
      });
    } else {
      data = [
        activeSchools - 5,
        activeSchools - 2,
        activeSchools,
        activeSchools + 3,
        activeSchools + 1,
        activeSchools + 4,
        activeSchools,
      ];
    }

    return {
      labels,
      datasets: [
        {
          label: "نشاط المدارس",
          data,
          borderColor: "#28917f",
          backgroundColor: "rgba(40, 145, 127, 0.1)",
          fill: true,
          tension: 0.4,
          pointRadius: 4,
        },
      ],
    };
  }, [weeklyActivityData, activeSchools, weeklyActivitySchoolsByDate]);

  const statusDonutData = {
    labels: ["فعال", "موقوف"],
    datasets: [
      {
        data: [activeSchools, inactiveSchools],
        backgroundColor: ["#16a34a", "#dc2626"],
        borderWidth: 0,
        hoverOffset: 4,
      },
    ],
  };


  // --- Handlers ---

  const calculateDaysRemaining = () => {
    if (!expirydateData?.expirydate) return null;
    const expiryDate = new Date(expirydateData.expirydate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expiryDate.setHours(0, 0, 0, 0);
    return Math.ceil(
      (expiryDate.getTime() - today.getTime()) / (1000 * 3600 * 24)
    );
  };

  const daysRemaining = calculateDaysRemaining();
  const getDaysParts = (days) => {
    if (days === null) return { num: "-", word: "" };
    if (days <= 0) return { num: "", word: "منتهي" };
    return { num: days, word: days <= 10 ? "أيام" : "يوماً" };
  };
  const { num, word } = getDaysParts(daysRemaining);

  const handleCleanOldStatus = async () => {
    if (isCleaningOldConnections) return;


    setIsCleaningOldConnections(true);

    try {
      const res = await dispatch(resetOldConnectionsStatus());

      if (res.meta.requestStatus !== "fulfilled") {
        throw new Error(
          res.payload || res.error?.message || "فشل في تحديث السيرفر"
        );
      }


      // إعادة جلب البيانات
      await dispatch(fetchData(true));
    } catch (error) {
      console.error("حدث خطأ أثناء التنظيف", error);
    } finally {
      setIsCleaningOldConnections(false);
    }
  };

  return (
    <div className="dashboard-kpi">
      <div className="dashboard-kpi__main">
        {/* Sidebar Metrics */}
        <aside className="metrics-sidebar">
          <div
            className={`metric-card ${
              unreadMessagesCount > 0 ? "metric-card--unread" : ""
            }`}
            onClick={() => navigate("/school-notes")}
            style={{ cursor: "pointer" }}
          >
            <span className="metric-card__label">رسائل المستخدمين</span>
            <div className="metric-card__value">{unreadMessagesCount}</div>
            <div
              className={`metric-card__trend ${
                unreadMessagesCount > 0 ? "up" : ""
              }`}
            >
              <i
                className={`fa-solid ${
                  unreadMessagesCount > 0
                    ? "fa-envelope-open-text"
                    : "fa-envelope"
                }`}
              ></i>
              {unreadMessagesCount > 0 ? " رسائل جديدة" : " لا يوجد رسائل"}
            </div>
          </div>
          <div className="metric-card">
            <span className="metric-card__label">إجمالي المدارس</span>
            <div className="metric-card__value">{totalSchools}</div>
            <div className="metric-card__trend up">
              
            </div>
          </div>
          <div className="metric-card metric-card--expanded">
            <span className="metric-card__label">المدارس العاملة</span>
            <div className="metric-card__value">{activeSchools}</div>
            <div className="metric-card__trend down"></div>
          </div>
          <div className="metric-card metric-card--compact">
            <span className="metric-card__label">مدارس متصلة</span>
            <div className="metric-card__value">{connectedNow}</div>
            <div className="metric-card__trend up"></div>
          </div>

          {daysRemaining !== null && (
            <div className="metric-card metric-card--expiry">
              <span className="metric-card__label">ينتهي النظام بعد</span>
              <div
                className="metric-card__value"
                style={{ fontSize: "20px", color: "var(--danger)" }}
              >
                {num} <small style={{ fontSize: "12px" }}>{word}</small>
              </div>
              <div className="metric-card__trend">
                <i
                  className="fa-solid fa-clock-rotate-left"
                  style={{ color: "var(--danger)" }}
                ></i>{" "}
                متبقي
              </div>
            </div>
          )}
        </aside>

        {/* Re-structured Dashboard Grid */}
        <div className="dashboard-flex-layout">
          {/* Main Column (Charts) */}
          <div className="dashboard-main-col">
            <div className="chart-box weekly-trend">
              <h3 className="chart-box__title">
                {selectedDayData ? selectedDayData.label : "نشاط المدارس الاسبوعي"}
                {selectedDayData && (
                  <button 
                    className="back-to-chart-btn"
                    onClick={() => setSelectedDayData(null)}
                  >
                    <FontAwesomeIcon icon={faArrowRight} />
                    العودة للرسم البياني
                  </button>
                )}
              </h3>
              <div className="chart-box__wrapper">
                {selectedDayData ? (
                  <div className="schools-list-card">
                    <div className="schools-list-header">
                      <div className="schools-count-badge">
                        <FontAwesomeIcon icon={faSchool} />
                        <span>{selectedDayData.value}</span>
                      </div>
                    </div>
                    <div className="schools-list-content">
                      {selectedDayData.schools.length > 0 ? (
                        selectedDayData.schools.map((school, index) => (
                          <div key={index} className="school-list-item">
                            <span className="school-number">{index + 1}</span>
                            <span
                              className="school-name school-name--link"
                              onClick={() => school.rgmw && navigate(`/schools/${school.rgmw}`)}
                            >
                              {school.name}
                            </span>
                          </div>
                        ))
                      ) : (
                        <div className="no-schools-message">
                          <FontAwesomeIcon icon={faInfoCircle} />
                          <span>لا توجد مدارس نشطة في هذا اليوم</span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <Line data={lineChartData} options={chartOptions} />
                )}
              </div>
            </div>

            <div className="chart-row donuts-row">
              <div className="chart-box chart-box--status">
                <h3 className="chart-box__title">احصائية توزيع المدارس</h3>
                <div className="chart-box__wrapper donut-wrap">
                  <Doughnut data={statusDonutData} options={donutOptions} />
                  <div className="donut-center">
                    <div className="num">{activeSchools}</div>
                    <div className="txt">فعال</div>
                  </div>
                </div>
              </div>

              <div className="chart-box quick-actions-box">
                <h3 className="chart-box__title chart-box__title--with-action">
                  <div className="title-text">
                    <i
                      className="fa-solid fa-bolt"
                      style={{ color: "var(--accent)", marginLeft: "8px" }}
                    ></i>
                    وصول سريع
                  </div>
                  <div className="action-buttons">
                    {isEditingActions && (
                      <PrettyTooltip content="إضافة اختصار">
                        <button
                          className="add-action-btn-main"
                          onClick={() => setShowAddMenu(!showAddMenu)}
                       
                        >
                          <i className="fa-solid fa-plus"></i>
                        </button>
                      </PrettyTooltip>
                    )}
                    <PrettyTooltip content="تخصيص">
                      <button
                        className={`customize-btn ${isEditingActions ? "active" : ""}`}
                        onClick={() => {
                          setIsEditingActions(!isEditingActions);
                          setShowAddMenu(false);
                        }}
                     
                      >
                        <i className="fa-solid fa-gear"></i>
                      </button>
                    </PrettyTooltip>
                  </div>
                </h3>
                <div className="chart-box__wrapper">
                  <div className="quick-actions-grid">
                    {quickActions.map((action, index) => (
                      <div
                        key={action.id}
                        className={`quick-action-item ${isEditingActions ? "editing" : ""}`}
                        onClick={() => !isEditingActions && navigate(action.path)}
                        style={{ "--action-color": action.color }}
                      >
                        <div className="action-icon">
                          <i className={`fa-solid ${action.icon}`}></i>
                        </div>
                        <span className="action-label">{action.label}</span>
                        
                        {isEditingActions && (
                          <>
                            <button
                              className="remove-action-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveAction(index);
                              }}
                            >
                              <i className="fa-solid fa-xmark"></i>
                            </button>
                            <div className="reorder-btns">
                              <button
                                disabled={index === 0}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMoveAction(index, -1);
                                }}
                              >
                                <i className="fa-solid fa-chevron-right"></i>
                              </button>
                              <button
                                disabled={index === quickActions.length - 1}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMoveAction(index, 1);
                                }}
                              >
                                <i className="fa-solid fa-chevron-left"></i>
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                    

                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Side Column (Live Monitoring - Full Height) */}
          <div className="dashboard-side-col">
            <div className="chart-box connected-schools-box">
              <h3 className="chart-box__title chart-box__title--with-action">
                <div className="title-text search-wrapper">
                  <i
                    className="fa-solid fa-magnifying-glass search-icon"
                    style={{
                      marginLeft: "8px",
                      fontSize: "12px",
                      color: "var(--muted)",
                    }}
                  ></i>
                  <input
                    type="text"
                    className="schools-search-input"
                    placeholder="البحث في المدارس المتصلة..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <span className="online-count-badge">{connectedNow}</span>
                </div>
                <PrettyTooltip content="تنظيف اتصالات الأيام السابقة">
                  <button
                    className="clean-status-btn"
                    onClick={handleCleanOldStatus}
                 
                    disabled={isCleaningOldConnections}
                  >
                    <i className="fa-solid fa-rotate"></i>
                  </button>
                </PrettyTooltip>
              </h3>
              <div className="chart-box__wrapper">
                {filteredConnectedSchools.length > 0 ? (
                  <div className="connected-schools-list">
                    {filteredConnectedSchools.map((school, idx) => (
                      <div
                        key={idx}
                        className="connected-school-item"
                        onClick={() => navigate(`/schools/${school.rgmw}`)}
                      >
                        <div className="school-info">
                          <span className="school-dot"></span>
                          <div className="school-text-meta">
                            <span className="school-name">
                              {school.schoolname}
                            </span>
                            <span className="school-secretary">
                              <span className="sec-label">
                                {school.secretaryLabel}:
                              </span>{" "}
                              {school.secretaryName}
                            </span>
                          </div>
                        </div>
                        <div className="school-login-time">
                          <div className="time-row">
                            <i className="fa-regular fa-clock"></i>
                            <span>
                              {school.loginInfo.time}{" "}
                              <small>{school.loginInfo.period}</small>
                            </span>
                          </div>
                          <div className="date-row">
                            <span className="day">{school.loginInfo.day}</span>
                            <span className="date">
                              {school.loginInfo.date}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      minHeight: "150px",
                      color: "var(--muted)",
                      opacity: 0.9,
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                  >
                    لا توجد مدارس نشطة حاليا
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer removed and moved to sidebar */}

      {/* Add Action Modal Popup */}
      {isEditingActions && showAddMenu && (
        <div className="available-actions-overlay" onClick={() => setShowAddMenu(false)}>
          <div className="available-actions-modal" onClick={(e) => e.stopPropagation()}>
            <div className="menu-header">
              <h3>إضافة اختصار جديد</h3>
              <button className="close-menu" onClick={() => setShowAddMenu(false)}>
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <div className="menu-list">
              {allAvailableActions
                .filter(a => !quickActions.some(qa => qa.id === a.id))
                .map(action => (
                  <div
                    key={action.id}
                    className="available-action-option"
                    onClick={() => handleAddAction(action)}
                  >
                    <div className="option-icon" style={{ backgroundColor: `${action.color}15`, color: action.color }}>
                      <i className={`fa-solid ${action.icon}`}></i>
                    </div>
                    <span className="option-label">{action.label}</span>
                    <i className="fa-solid fa-plus-circle add-icon"></i>
                  </div>
                ))}
              
              {allAvailableActions.filter(a => !quickActions.some(qa => qa.id === a.id)).length === 0 && (
                <div className="no-more-actions">تم إضافة جميع الاختصارات المتاحة</div>
              )}
            </div>
            <div className="menu-footer">
              <button className="reset-all-btn" onClick={handleResetActions}>
                استعادة الافتراضي
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
